import { Button } from '@unified-checkout/design-system';
import { ContactForm } from '@unified-checkout/kit-components';
import { useState, useContext } from 'react';
import { IAddress, AddressType, AddressTypeMap } from '../../../../types/innerLayout/addresses';
import {
  useAddAddress,
  useEditAddress,
} from '../../../../services/inner_layout/addresses';
import { DeviceContext } from '../../../../context/device';
import { ContactFormV2 } from './ContactFormV2';

interface AddAddressProps {
  isEditing?: boolean;
  addressData?: IAddress;
  addressType: AddressType;
  setIsAddingAddress: ({isAdding, addressType}: {isAdding: boolean, addressType?: AddressType}) => void;
  setSelectedAddressId: (id: number | undefined) => void;
  isFirstAddress?: boolean;
  showIsDefaultCheckbox?: boolean;
}


export const AddAddress = ({
  isEditing,
  addressData,
  addressType,
  setIsAddingAddress,
  setSelectedAddressId,
  isFirstAddress = false,
  showIsDefaultCheckbox = true,
}: AddAddressProps) => {
  const { isMobile } = useContext(DeviceContext);
  const [address, setAddress] = useState<{
    SHIPPING?: any;
    BILLING?: any;
  } | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [alsoAddToBilling, setAlsoAddToBilling] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  const { mutate: addAddressMutation, isPending: isAddingAddress } = useAddAddress();
  const { mutate: editAddressMutation, isPending: isEditingAddress } = useEditAddress();

  const handleClientData = (formData: any) => {
    if (formData) {
      const newAddress: {
        SHIPPING?: any;
        BILLING?: any;
      } = {};

      if (addressType === AddressTypeMap.SHIPPING && formData.shipping) {
        newAddress[AddressTypeMap.SHIPPING] = formData.shipping;
      } else if (addressType === AddressTypeMap.BILLING && formData.billing) {
        newAddress[AddressTypeMap.BILLING] = formData.billing;
      }

      setAddress(newAddress);
    }
  };

  const handleSubmit = () => {
    if (!address || !addressType) return;
    const data = address[addressType];
    if (!data) return;
    const payload = {
      ...data,
      is_default: isDefault || isFirstAddress,
    };

    const onSuccess = () => {
      setSelectedAddressId(undefined);
      setIsAddingAddress({isAdding: false, addressType});
    };

    if (isEditing && addressData?.id) {
      if (addressType === AddressTypeMap.BILLING) {
        editAddressMutation({
          address_type: 'BILLING',
          body: payload,
          id: addressData.id,
        }, {
          onSuccess
        });
        return;
      } else if (addressType === AddressTypeMap.SHIPPING) {
        editAddressMutation({
          address_type: AddressTypeMap.SHIPPING,
          body: payload,
          id: addressData.id,
        }, {
          onSuccess: () => {
            if (alsoAddToBilling) {
              addAddressMutation({
                address_type: AddressTypeMap.BILLING,
                body: payload,
              }, {
                onSuccess
              });
            } else {
              onSuccess();
            }
          }
        });
        return;
      }
    }

    if (addressType === AddressTypeMap.BILLING) {
      addAddressMutation(
        {
          address_type: AddressTypeMap.BILLING,
          body: payload,
        },
        {
          onSuccess,
        }
      );
    } else {
      addAddressMutation(
        {
          address_type: AddressTypeMap.SHIPPING,
          body: { ...payload, use_same_address: alsoAddToBilling },
        },
        {
          onSuccess: onSuccess,
        }
      );
    }
  };
  const collectShippingAddress = addressType === AddressTypeMap.SHIPPING;
  const collectBillingAddress = addressType === AddressTypeMap.BILLING;
  const needsName = !(collectShippingAddress || collectBillingAddress);
  const needsPhone = !(collectShippingAddress || collectBillingAddress);

  return (
    <div className="flex flex-col gap-4">
      {!isEditing && addressType && (
        <h2 className="pb-2 text-gray-700 english-utility-label-medium md:english-body-lg-semibold">
          Add {addressType === AddressTypeMap.SHIPPING ? 'Shipping' : 'Billing'}{' '}
          address
        </h2>
      )}
      {isMobile ? (
        <div className="flex flex-col gap-4">
          <ContactForm
            onClientData={handleClientData}
            collectShippingAddress={collectShippingAddress}
            collectBillingAddress={collectBillingAddress}
            needsName={needsName}
            needsPhone={needsPhone}
            hideLabels
            defaultCountryISO="EG"
            defaultValues={{
              ...(collectShippingAddress && { shipping: addressData }),
              ...(collectBillingAddress && { billing: addressData }),
            }}
            onValidityChange={(isValid,isDirty)=>setIsValid(isValid && isDirty)}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <ContactFormV2
            onClientData={handleClientData}
            collectShippingAddress={collectShippingAddress}
            collectBillingAddress={collectBillingAddress}
            needsName={needsName}
            needsPhone={needsPhone}
            hideLabels
            defaultCountryISO="EG"
            defaultValues={{
              ...(collectShippingAddress && { shipping: addressData }),
              ...(collectBillingAddress && { billing: addressData }),
            }}
            onValidityChange={(isValid,isDirty)=>setIsValid(isValid && isDirty)}
          />
        </div>
      )}
      {!addressData?.is_default && showIsDefaultCheckbox && (
        <div className='flex items-center gap-2.5'>
          <input
            type="checkbox"
            id="default-checkbox"
            checked={isDefault}
            onChange={() => setIsDefault((prev) => !prev)}
            className="w-4 h-4"
          />
          <label
            htmlFor="default-checkbox"
            className="english-utility-label-medium text-[#475467] cursor-pointer"
          >
            Set this as default
          </label>
        </div>
      )}
      {addressType === AddressTypeMap.SHIPPING && !isEditing && (
        <div className='flex items-center gap-2.5'>
          <input
            type="checkbox"
            id="billing-checkbox"
            checked={alsoAddToBilling}
            onChange={() => setAlsoAddToBilling((prev) => !prev)}
            className="w-4 h-4"
          />
          <label
            htmlFor="billing-checkbox"
            className="english-utility-label-medium text-[#475467] cursor-pointer"
          >
            Save the same address for billing
          </label>
        </div>
      )}
      <div className="w-full [&_button]:w-full md:mt-1">
        <Button
          size="md"
          disabled={isEditing ? (!isValid && !isDefault && !alsoAddToBilling) || isEditingAddress : !isValid || isAddingAddress}
          text="Save address"
          type="button"
          onClickHandler={handleSubmit}
        />
      </div>
    </div>
  );
};
